'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var filterX = _interopDefault(require('ml-array-xy-filter-x'));
var equallySpaced = _interopDefault(require('ml-array-xy-equally-spaced'));
var Stat = _interopDefault(require('ml-stat/array'));
var mlSpectraProcessing = require('ml-spectra-processing');
var jcampconverter = require('jcampconverter');

/**
 *
 * @param {*} spectrum
 * @param {object} [options={}]
 *
 */

function getData(spectrum, options = {}) {
  const { filter } = options;

  let data = { x: [], y: [] };

  if (filter) {
    data = filterX({ x: spectrum.x, y: spectrum.y }, filter);
  }
  return data;
}

function updateNormalized(spectrum, options = {}) {
  let {
    from = spectrum.x[0],
    to = spectrum.x[spectrum.x.length - 1],
    numberOfPoints = 1024,
    processes = [],
    exclusions = []
  } = options;

  let y = spectrum.y.slice(0);
  for (let process of processes) {
    switch (process.kind) {
      case 'centerMean': {
        let mean = Stat.mean(spectrum.y);
        let meanFct = (y) => y - mean;
        y = y.map(meanFct);
        break;
      }
      case 'scaleSD': {
        let std = Stat.standardDeviation(spectrum.y);
        let stdFct = (y) => y / std;
        y = y.map(stdFct);
        break;
      }
      default:
        throw new Error(`Unknown process kind: ${process.kind}`);
    }
  }

  let result = equallySpaced(
    { x: spectrum.x, y },
    { from, to, numberOfPoints, exclusions }
  );
  spectrum.normalized = result;

  return result;
}

function updateRangesInfo(spectrum, ranges = []) {
  spectrum.ranges = {};
  for (let range of ranges) {
    range = JSON.parse(JSON.stringify(range));
    spectrum.ranges[range.label] = range;
    let fromToIndex = {
      fromIndex: mlSpectraProcessing.arrayFindClosestIndex(spectrum.normalized.x, range.from),
      toIndex: mlSpectraProcessing.arrayFindClosestIndex(spectrum.normalized.x, range.to)
    };
    range.integration = mlSpectraProcessing.xyIntegration(spectrum.normalized, fromToIndex);
    range.maxPoint = mlSpectraProcessing.xyMaxYPoint(spectrum.normalized, fromToIndex);
  }
}

/**
 * Class allowing manipulate one IR spectrum
 * @class spectrum
 * @param {object} [json={}] - object containing a spectrum
 * @param {Array} [json.x=[]] - x values
 * @param {Array} [json.y=[]] - y values
 */
class Spectrum {
  constructor(x, y, id, options = {}) {
    const { meta = {} } = options;
    if (!id) throw new Error('Spectrum: id is mandatory');

    if (x && x.length > 1 && x[0] > x[1]) {
      this.x = x.reverse();
      this.y = y.reverse();
    } else {
      this.x = x || [];
      this.y = y || [];
    }
    this.id = id;
    this.meta = meta;
    this.normalized = undefined;
  }

  getXY() {
    return { x: this.x, y: this.y };
  }
}

Spectrum.prototype.getData = function (options) {
  return getData(this, options);
};

Spectrum.prototype.updateNormalized = function (options) {
  return updateNormalized(this, options);
};

Spectrum.prototype.updateRangesInfo = function (ranges) {
  updateRangesInfo(this, ranges);
};

function getJcampKind(data) {
  let datatype = data.spectra[0].dataType.toLowerCase();
  let yUnit = data.spectra[0].yUnit.toLowerCase();

  if (datatype.match(/infrared/)) {
    if (yUnit.match(/absorbance/)) {
      return IR_ABSORBANCE;
    } else {
      return IR_TRANSMITTANCE;
    }
  }
  return undefined;
}

const IR_TRANSMITTANCE = {
  normalization: {},
  importation: {
    converter: (transmittance) => -Math.log10(transmittance)
  },
  kind: 'Infrared',
  display: {
    xLabel: 'wavelength [cm-1]',
    xInverted: true,
    yLabel: 'Absorbance'
  }
};

const IR_ABSORBANCE = {
  normalization: {},
  kind: 'Infrared',
  display: {
    xLabel: 'wavelength [cm-1]',
    xInverted: true,
    yLabel: 'Absorbance'
  }
};

/**
 * Creates a new Chromatogram element based in a JCAMP string
 * @param {string} jcamp - String containing the JCAMP data
 * @return {Spectrum} - New class element with the given data
 */
function jcamp(jcamp) {
  const parsed = jcampconverter.convert(jcamp, { xy: true });
  const kind = getJcampKind(parsed);
  const data = parsed.spectra[0].data[0];
  // we convert the data
  if (kind && kind.importation && kind.importation.converter) {
    data.y = data.y.map(kind.importation.converter);
  }

  return { data, kind };
}

function getFilterAnnotations(filter = {}) {
  let { exclusions = [] } = filter;
  let annotations = [];
  exclusions = exclusions.filter((exclusion) => !exclusion.ignore);
  annotations = exclusions.map((exclusion) => {
    var annotation = {
      type: 'rect',
      position: [{ x: exclusion.from, y: -200 }, { x: exclusion.to, y: 200 }],
      strokeWidth: 0,
      fillColor: 'rgba(255,255,224,1)'
    };
    return annotation;
  });
  if (filter.from !== undefined) {
    annotations.push({
      type: 'rect',
      position: [{ x: 0, y: -200 }, { x: filter.from, y: 200 }],
      strokeWidth: 0,
      fillColor: 'rgba(255,255,224,1)'
    });
  }
  if (filter.to !== undefined) {
    annotations.push({
      type: 'rect',
      position: [{ x: filter.to, y: -200 }, { x: 10000, y: 200 }],
      strokeWidth: 0,
      fillColor: 'rgba(255,255,224,1)'
    });
  }
  return annotations;
}

class SpectraProcessor {
  constructor(options = {}) {
    this.keepOriginalData =
      options.keepOriginalData === undefined ? false : options.keepOriginalData;
    this.normalizationFilter = undefined;
    this.spectra = [];
  }

  getFilterAnnotations() {
    return getFilterAnnotations(this.normalizationFilter);
  }

  setNormalizationFilter(normalizationFilter = {}) {
    if (!this.keepOriginalData && this.spectra.length > 0) {
      throw new Error(
        'Can not change normalization filter, missing original data. Use the option keepOriginalData=true.'
      );
    }
    this.normalizationFilter = normalizationFilter;
    for (let spectrum of this.spectra) {
      spectrum.updateNormalized(this.normalizationFilter);
    }
  }

  /**
   * Add jcamp
   * @param {string} jcamp
   * @param {string} id
   * @param {boolean} [force=false]
   * @param {object} [meta={}]
   * @param {string} [meta.color]
   */
  addFromJcamp(jcamp$1, id, meta = {}, force = false) {
    if (force === false && this.contains(id)) {
      return;
    }

    let parsed = jcamp(jcamp$1);
    this.createAndAddSpectrum(parsed, id, meta);
  }

  updateRangesInfo(options) {
    for (let spectrum of this.spectra) {
      spectrum.updateRangesInfo(options);
    }
  }

  /**
   * Add a spectrum
   * @param {Spectrum} spectrum
   */
  createAndAddSpectrum(parsed, id, meta) {
    let index = this.getSpectrumIndex(id);
    if (index === undefined) index = this.spectra.length;
    let spectrum = new Spectrum(parsed.data.x, parsed.data.y, id, {
      meta
    });
    spectrum.updateNormalized(this.normalizationFilter);
    this.spectra[index] = spectrum;
  }

  removeSpectrum(id) {
    let index = this.getSpectrumIndex(id);
    if (index === undefined) return undefined;
    return this.spectra.splice(index, 1);
  }

  removeSpectraNotIn(ids) {
    let currentIDs = this.spectra.map((spectrum) => spectrum.id);
    for (let id of currentIDs) {
      if (!ids.includes(id)) {
        this.removeSpectrum(id);
      }
    }
  }

  contains(id) {
    return !isNaN(this.getSpectrumIndex(id));
  }

  getSpectrumIndex(id) {
    if (!id) return undefined;
    for (let i = 0; i < this.spectra.length; i++) {
      let spectrum = this.spectra[i];
      if (spectrum.id === id) return i;
    }
    return undefined;
  }

  getSpectrum(id) {
    let index = this.getSpectrumIndex(id);
    if (index === undefined) return undefined;
    return this.spectra[index];
  }

  getNormalizedData() {
    if (!this.spectra || !this.spectra[0]) return {};
    let matrix = [];
    let meta = [];
    let ids = [];
    for (let spectrum of this.spectra) {
      ids.push(spectrum.id);
      matrix.push(spectrum.normalized.y);
      meta.push(spectrum.meta);
    }
    let x = this.spectra[0].normalized.x;
    return { ids, matrix, meta, x };
  }

  getNormalizedChart(options = {}) {
    const { ids } = options;
    let chart = {
      data: []
    };
    for (let spectrum of this.spectra) {
      if (!ids || ids.includes(spectrum.id)) {
        let data = spectrum.normalized;
        data.styles = {
          unselected: {
            lineColor: spectrum.meta.color || 'darkgrey',
            lineWidth: 1,
            lineStyle: 1
          },
          selected: {
            lineColor: spectrum.meta.color || 'darkgrey',
            lineWidth: 3,
            lineStyle: 1
          }
        };
        data.label = spectrum.meta.id || spectrum.id;
        chart.data.push(data);
      }
    }
    return chart;
  }

  getChart(options = {}) {
    const { ids, filter = {} } = options;
    let chart = {
      data: []
    };
    for (let spectrum of this.spectra) {
      if (!ids || ids.includes(spectrum.id)) {
        let data = spectrum.getData({ filter });
        data.styles = {
          unselected: {
            lineColor: spectrum.meta.color || 'darkgrey',
            lineWidth: 1,
            lineStyle: 1
          },
          selected: {
            lineColor: spectrum.meta.color || 'darkgrey',
            lineWidth: 3,
            lineStyle: 1
          }
        };
        data.label = spectrum.meta.id || spectrum.id;
        chart.data.push(data);
      }
    }
    return chart;
  }
}

exports.SpectraProcessor = SpectraProcessor;
